
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';
import ReactionButton from './ReactionButton';

interface Post {
  id: string;
  title: string;
  content: string;
  content_html?: string;
  author_id: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  is_pinned: boolean;
  images?: string[] | null;
  tags?: string[] | null;
  user_profiles: {
    username: string;
    avatar_url: string;
    avatar_id?: string;
  };
}

interface Comment {
  id: string;
  content: string;
  author_id: string;
  post_id: string;
  parent_id: string | null;
  created_at: string;
  upvotes: number;
  user_profiles?: {
    username: string;
    avatar_url: string;
    avatar_id?: string;
  };
  replies?: Comment[];
  reply_count?: number;
}

interface Member {
  user_id: string;
  role: 'owner' | 'admin' | 'moderator' | 'member';
}

interface DiscussionFeedProps {
  posts: Post[];
  groupId: string;
  currentUserId: string | null;
  userRole: string | null;
  members: Member[];
  isMember: boolean;
  onCreatePost: () => void;
  onPinPost?: (postId: string) => void;
  onDeletePost?: (postId: string) => void;
  onRefresh?: () => void;
}

// Time ago helper
const timeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Shared avatar renderer
const renderAvatar = (
  avatarUrl?: string,
  _avatarId?: string,
  username?: string,
  size = 40
) => {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={username || ''}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {(username || 'U').charAt(0).toUpperCase()}
    </div>
  );
};

// Role badge
const RoleBadge = ({ role }: { role: string }) => {
  if (role === 'member') return null;
  const config: Record<
    string,
    { label: string; bg: string; text: string; icon: string }
  > = {
    owner: {
      label: 'Owner',
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      icon: 'ri-vip-crown-fill',
    },
    admin: {
      label: 'Admin',
      bg: 'bg-rose-100',
      text: 'text-rose-700',
      icon: 'ri-shield-star-fill',
    },
    moderator: {
      label: 'Mod',
      bg: 'bg-teal-100',
      text: 'text-teal-700',
      icon: 'ri-shield-check-fill',
    },
  };
  const c = config[role];
  if (!c) return null;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.bg} ${c.text}`}
    >
      <i className={`${c.icon} text-[9px]`}></i>
      {c.label}
    </span>
  );
};

// Image gallery with lightbox
const ImageGallery = ({ images }: { images: string[] }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!images.length) return null;

  return (
    <>
      <div
        className={`mt-3 grid gap-1 rounded-xl overflow-hidden ${
          images.length === 1
            ? 'grid-cols-1'
            : images.length === 2
            ? 'grid-cols-2'
            : images.length === 3
            ? 'grid-cols-2'
            : 'grid-cols-2'
        }`}
      >
        {images.slice(0, 4).map((url, index) => (
          <div
            key={index}
            className={`relative cursor-pointer group overflow-hidden ${
              images.length === 1
                ? 'aspect-video max-h-[400px]'
                : images.length === 3 && index === 0
                ? 'row-span-2 aspect-square'
                : 'aspect-square'
            }`}
            onClick={() => {
              setActiveIndex(index);
              setLightboxOpen(true);
            }}
          >
            <img
              src={url}
              alt=""
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {index === 3 && images.length > 4 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white text-2xl font-bold">
                  +{images.length - 4}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((p) => (p === 0 ? images.length - 1 : p - 1));
                }}
                className="absolute left-4 w-12 h-12 flex items-center justify-center text-white hover:bg-white/20 rounded-full cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-3xl"></i>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((p) =>
                    p === images.length - 1 ? 0 : p + 1
                  );
                }}
                className="absolute right-4 w-12 h-12 flex items-center justify-center text-white hover:bg-white/20 rounded-full cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-3xl"></i>
              </button>
            </>
          )}
          <img
            src={images[activeIndex]}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex(i);
                  }}
                  className={`w-2 h-2 rounded-full cursor-pointer ${
                    i === activeIndex ? 'bg-white' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

/* ─── Threaded Comment Component ─── */
const ThreadedComment = ({
  comment,
  postId,
  currentUserId,
  depth = 0,
  onReplyAdded,
}: {
  comment: Comment;
  postId: string;
  currentUserId: string | null;
  depth?: number;
  onReplyAdded: () => void;
}) => {
  const { profile } = useAuth();
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replies, setReplies] = useState<Comment[]>(comment.replies || []);
  const [showReplies, setShowReplies] = useState(
    depth < 1 && (comment.replies?.length || 0) > 0
  );
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyCount, setReplyCount] = useState(
    comment.reply_count || comment.replies?.length || 0
  );
  const replyInputRef = useRef<HTMLInputElement>(null);

  const maxDepth = 3;

  const loadReplies = async () => {
    if (replies.length > 0 && replies.length >= replyCount) {
      setShowReplies(true);
      return;
    }
    setLoadingReplies(true);
    try {
      const { data } = await supabase
        .from('comments')
        .select('*, user_profiles:author_id(username, avatar_url, avatar_id)')
        .eq('parent_id', comment.id)
        .eq('post_id', postId)
        .order('created_at', { ascending: true })
        .limit(50);
      if (data) {
        setReplies(
          data.map((r: any) => ({
            ...r,
            replies: [],
            reply_count: 0,
          }))
        );
        setShowReplies(true);
      }
    } catch (err) {
      console.error('Error loading replies:', err);
    } finally {
      setLoadingReplies(false);
    }
  };

  const toggleReplies = () => {
    if (showReplies) {
      setShowReplies(false);
    } else {
      loadReplies();
    }
  };

  const submitReply = async () => {
    if (!replyText.trim() || !currentUserId || submitting) return;
    setSubmitting(true);
    try {
      const parentId =
        depth >= maxDepth - 1 ? comment.parent_id || comment.id : comment.id;
      const { data, error } = await supabase
        .from('comments')
        .insert({
          content: replyText.trim(),
          author_id: currentUserId,
          post_id: postId,
          parent_id: parentId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: 'published',
          upvotes: 0,
          downvotes: 0,
        })
        .select(
          '*, user_profiles:author_id(username, avatar_url, avatar_id)'
        )
        .single();

      if (!error && data) {
        if (depth >= maxDepth - 1) {
          // Reply goes to parent level — let parent refresh
          onReplyAdded();
        } else {
          setReplies((prev) => [
            ...prev,
            { ...data, replies: [], reply_count: 0 },
          ]);
          setReplyCount((p) => p + 1);
          setShowReplies(true);
        }
        setReplyText('');
        setShowReplyInput(false);

        // Update post comment count
        const { data: postData } = await supabase
          .from('posts')
          .select('comments_count')
          .eq('id', postId)
          .maybeSingle();
        if (postData) {
          await supabase
            .from('posts')
            .update({
              comments_count: (postData.comments_count || 0) + 1,
            })
            .eq('id', postId);
        }
        onReplyAdded();
      }
    } catch (err) {
      console.error('Error submitting reply:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyClick = () => {
    setShowReplyInput(true);
    setTimeout(() => replyInputRef.current?.focus(), 50);
  };

  const indentClass = depth > 0 ? 'ml-8 pl-3 border-l-2 border-gray-100' : '';

  return (
    <div className={indentClass}>
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">
          {renderAvatar(
            comment.user_profiles?.avatar_url,
            comment.user_profiles?.avatar_id,
            comment.user_profiles?.username,
            depth > 0 ? 26 : 30
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-2xl px-3 py-2 border border-gray-100">
            <span className="text-xs font-semibold text-gray-900">
              {comment.user_profiles?.username || 'User'}
            </span>
            <p className="text-xs text-gray-700 mt-0.5 leading-relaxed">
              {comment.content}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-1 ml-2">
            <span className="text-[10px] text-gray-400">
              {timeAgo(comment.created_at)}
            </span>
            <button className="text-[10px] text-gray-500 hover:text-teal-600 font-semibold cursor-pointer">
              Like
            </button>
            {currentUserId && depth < maxDepth && (
              <button
                onClick={handleReplyClick}
                className="text-[10px] text-gray-500 hover:text-teal-600 font-semibold cursor-pointer"
              >
                Reply
              </button>
            )}
            {replyCount > 0 && (
              <button
                onClick={toggleReplies}
                className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold cursor-pointer flex items-center gap-0.5"
              >
                {loadingReplies ? (
                  <i className="ri-loader-4-line animate-spin text-[10px]"></i>
                ) : (
                  <>
                    <i
                      className={`ri-arrow-${
                        showReplies ? 'up' : 'down'
                      }-s-line text-[11px]`}
                    ></i>
                    {showReplies ? 'Hide' : `${replyCount}`} {replyCount === 1 ? 'reply' : 'replies'}
                  </>
                )}
              </button>
            )}
          </div>

          {/* Reply input */}
          {showReplyInput && currentUserId && (
            <div className="flex items-center gap-2 mt-2 ml-1">
              <div className="flex-shrink-0">
                {renderAvatar(
                  profile?.avatar_url,
                  profile?.avatar_id,
                  profile?.username,
                  24
                )}
              </div>
              <div className="flex-1 relative">
                <input
                  ref={replyInputRef}
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submitReply();
                    }
                  }}
                  placeholder={`Reply to ${
                    comment.user_profiles?.username || 'User'
                  }...`}
                  maxLength={500}
                  className="w-full bg-white border border-gray-200 rounded-full px-3 py-1.5 text-[11px] outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400/30 transition-colors pr-16"
                />
                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    onClick={() => {
                      setShowReplyInput(false);
                      setReplyText('');
                    }}
                    className="w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer"
                  >
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                  <button
                    onClick={submitReply}
                    disabled={!replyText.trim() || submitting}
                    className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                      replyText.trim()
                        ? 'text-teal-500 hover:bg-teal-50'
                        : 'text-gray-300'
                    }`}
                  >
                    {submitting ? (
                      <i className="ri-loader-4-line animate-spin text-[10px]"></i>
                    ) : (
                      <i className="ri-send-plane-2-fill text-[10px]"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Nested replies */}
          {showReplies && replies.length > 0 && (
            <div className="mt-2 space-y-2">
              {replies.map((reply) => (
                <ThreadedComment
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                  currentUserId={currentUserId}
                  depth={depth + 1}
                  onReplyAdded={() => {
                    loadReplies();
                    onReplyAdded();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Moment Card Component ─── */
const MomentCard = ({
  post,
  members,
  currentUserId,
  canModerate,
  onPinPost,
  onDeletePost,
  isNew,
}: {
  post: Post;
  members: Member[];
  currentUserId: string | null;
  canModerate: boolean;
  onPinPost?: (id: string) => void;
  onDeletePost?: (id: string) => void;
  isNew?: boolean;
}) => {
  const { profile } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsCount, setCommentsCount] = useState(
    post.comments_count || 0
  );
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);

  const memberRole =
    members.find((m) => m.user_id === post.author_id)?.role || 'member';
  const isLongContent = post.content.length > 300;
  const displayContent =
    isLongContent && !expanded
      ? post.content.substring(0, 300) + '...'
      : post.content;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
        setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const { data } = await supabase
        .from('comments')
        .select('*, user_profiles:author_id(username, avatar_url, avatar_id)')
        .eq('post_id', post.id)
        .is('parent_id', null)
        .order('created_at', { ascending: true })
        .limit(30);

      if (data) {
        const enriched = await Promise.all(
          data.map(async (c: any) => {
            const { count } = await supabase
              .from('comments')
              .select('*', { count: 'exact', head: true })
              .eq('parent_id', c.id);
            return { ...c, replies: [], reply_count: count || 0 };
          })
        );
        setComments(enriched);
      }
    } catch (err) {
      console.error('Error loading comments', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const toggleComments = () => {
    if (!showComments) loadComments();
    setShowComments(!showComments);
    setTimeout(() => commentInputRef.current?.focus(), 100);
  };

  const handleCommentClick = () => {
    if (!showComments) loadComments();
    setShowComments(true);
    setTimeout(() => commentInputRef.current?.focus(), 100);
  };

  const submitComment = async () => {
    if (!newComment.trim() || !currentUserId || submittingComment) return;
    setSubmittingComment(true);
    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          content: newComment.trim(),
          author_id: currentUserId,
          post_id: post.id,
          parent_id: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: 'published',
          upvotes: 0,
          downvotes: 0,
        })
        .select(
          '*, user_profiles:author_id(username, avatar_url, avatar_id)'
        )
        .single();

      if (!error && data) {
        setComments((prev) => [
          ...prev,
          { ...data, replies: [], reply_count: 0 },
        ]);
        setCommentsCount((p) => p + 1);
        setNewComment('');
        await supabase
          .from('posts')
          .update({ comments_count: commentsCount + 1 })
          .eq('id', post.id);
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleReplyAdded = () => {
    setCommentsCount((p) => p + 1);
  };

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-500 ${
        isNew ? 'ring-2 ring-teal-400/50 animate-pulse-once' : ''
      }`}
    >
      {isNew && (
        <div className="px-4 pt-2.5 flex items-center gap-1.5 text-teal-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
          </span>
          <span className="text-[11px] font-semibold">New moment</span>
        </div>
      )}

      {post.is_pinned && (
        <div className="px-4 pt-3 flex items-center gap-1.5 text-amber-600">
          <i className="ri-pushpin-fill text-xs"></i>
          <span className="text-xs font-semibold">Pinned Moment</span>
        </div>
      )}

      {/* Header */}
      <div className="px-4 pt-3 pb-0 flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex-shrink-0 ring-2 ring-gray-100 rounded-full">
            {renderAvatar(
              post.user_profiles.avatar_url,
              post.user_profiles.avatar_id,
              post.user_profiles.username,
              42
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-sm text-gray-900">
                {post.user_profiles.username}
              </span>
              <RoleBadge role={memberRole} />
            </div>
            <span className="text-xs text-gray-400">{timeAgo(post.created_at)}</span>
          </div>
        </div>

        {(canModerate || post.author_id === currentUserId) && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-more-2-fill"></i>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20">
                {canModerate && (
                  <button
                    onClick={() => {
                      onPinPost?.(post.id);
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <i
                      className={
                        post.is_pinned
                          ? 'ri-pushpin-fill text-amber-500'
                          : 'ri-pushpin-line'
                      }
                    ></i>
                    {post.is_pinned ? 'Unpin' : 'Pin to top'}
                  </button>
                )}
                {(canModerate || post.author_id === currentUserId) && (
                  <button
                    onClick={() => {
                      onDeletePost?.(post.id);
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pt-2 pb-1">
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
          {displayContent}
        </p>
        {isLongContent && !expanded && (
          <button
            onClick={() => setExpanded(true)}
            className="text-teal-600 hover:text-teal-700 text-sm font-medium mt-1 cursor-pointer"
          >
            See more
          </button>
        )}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {post.tags.map((tag, i) => (
              <span
                key={i}
                className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Images */}
      {post.images && post.images.length > 0 && (
        <div className="px-4 pb-2">
          <ImageGallery images={post.images} />
        </div>
      )}

      {/* Reactions */}
      <ReactionButton
        postId={post.id}
        currentUserId={currentUserId}
        initialLikesCount={post.likes_count || 0}
      />

      {/* Comment & Share row */}
      <div className="px-4 -mt-[1px] border-t border-gray-100">
        <div className="flex items-center">
          <button
            onClick={handleCommentClick}
            disabled={!currentUserId}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors cursor-pointer rounded-lg my-0.5"
          >
            <i className="ri-chat-3-line text-base"></i>
            Comment
            {commentsCount > 0 && (
              <span className="text-xs text-gray-400">
                ({commentsCount})
              </span>
            )}
          </button>
          <button
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors cursor-pointer rounded-lg my-0.5"
            onClick={() => {
              if (navigator.share) {
                navigator
                  .share({
                    title: post.content.substring(0, 60),
                    text: post.content.substring(0, 200),
                  })
                  .catch(() => {});
              }
            }}
          >
            <i className="ri-share-forward-line text-base"></i>
            Share
          </button>
        </div>
      </div>

      {/* Threaded Comments section */}
      {showComments && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          <div className="px-4 pt-3 space-y-3 max-h-[420px] overflow-y-auto">
            {loadingComments ? (
              <div className="flex items-center justify-center py-4">
                <i className="ri-loader-4-line animate-spin text-gray-400 text-lg"></i>
              </div>
            ) : comments.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-3">
                No comments yet. Be the first!
              </p>
            ) : (
              comments.map((comment) => (
                <ThreadedComment
                  key={comment.id}
                  comment={comment}
                  postId={post.id}
                  currentUserId={currentUserId}
                  depth={0}
                  onReplyAdded={handleReplyAdded}
                />
              ))
            )}
          </div>

          {/* Top-level comment input */}
          {currentUserId && (
            <div className="px-4 py-3 flex items-center gap-2">
              <div className="flex-shrink-0">
                {renderAvatar(
                  profile?.avatar_url,
                  profile?.avatar_id,
                  profile?.username,
                  30
                )}
              </div>
              <div className="flex-1 relative">
                <input
                  ref={commentInputRef}
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submitComment();
                    }
                  }}
                  placeholder="Write a comment..."
                  maxLength={500}
                  className="w-full bg-white border border-gray-200 rounded-full px-4 py-2 text-xs outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400/30 transition-colors pr-10"
                />
                <button
                  onClick={submitComment}
                  disabled={!newComment.trim() || submittingComment}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                    newComment.trim()
                      ? 'text-teal-500 hover:bg-teal-50'
                      : 'text-gray-300'
                  }`}
                >
                  {submittingComment ? (
                    <i className="ri-loader-4-line animate-spin text-xs"></i>
                  ) : (
                    <i className="ri-send-plane-2-fill text-xs"></i>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Main Feed Component ─── */
const DiscussionFeed = ({
  posts,
  groupId,
  currentUserId,
  userRole,
  members,
  isMember,
  onCreatePost,
  onPinPost,
  onDeletePost,
  onRefresh,
}: DiscussionFeedProps) => {
  const [sortBy, setSortBy] = useState<'recent' | 'popular'>('recent');
  const [localIsMember, setLocalIsMember] = useState(isMember);
  const [realtimePosts, setRealtimePosts] = useState<Post[]>([]);
  const [newPostIds, setNewPostIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLocalIsMember(isMember);
  }, [isMember]);

  const allPosts = useCallback(() => {
    const parentIds = new Set(posts.map((p) => p.id));
    const uniqueRealtime = realtimePosts.filter(
      (rp) => !parentIds.has(rp.id)
    );
    return [...uniqueRealtime, ...posts];
  }, [posts, realtimePosts]);

  useEffect(() => {
    if (!groupId || !isMember) return;
    const channel = supabase
      .channel(`group-moments-${groupId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'posts',
          filter: `group_id=eq.${groupId}`,
        },
        async (payload) => {
          const newPost = payload.new as any;
          if (newPost.author_id === currentUserId) return;
          if (posts.find((p) => p.id === newPost.id)) return;
          if (realtimePosts.find((p) => p.id === newPost.id)) return;
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('username, avatar_url, avatar_id')
            .eq('id', newPost.author_id)
            .maybeSingle();
          const enrichedPost: Post = {
            ...newPost,
            user_profiles:
              profileData || { username: 'User', avatar_url: '' },
          };
          setRealtimePosts((prev) => [enrichedPost, ...prev]);
          setNewPostIds((prev) => new Set(prev).add(newPost.id));
          setTimeout(() => {
            setNewPostIds((prev) => {
              const next = new Set(prev);
              next.delete(newPost.id);
              return next;
            });
          }, 8000);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'posts',
          filter: `group_id=eq.${groupId}`,
        },
        (payload) => {
          const deletedId = (payload.old as any).id;
          setRealtimePosts((prev) =>
            prev.filter((p) => p.id !== deletedId)
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'posts',
          filter: `group_id=eq.${groupId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          setRealtimePosts((prev) =>
            prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [groupId, isMember, currentUserId, posts, realtimePosts]);

  useEffect(() => {
    setRealtimePosts((prev) => {
      const parentIds = new Set(posts.map((p) => p.id));
      return prev.filter((rp) => !parentIds.has(rp.id));
    });
  }, [posts]);

  const mergedPosts = allPosts();
  const filteredPosts = [...mergedPosts].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    if (sortBy === 'popular')
      return (b.likes_count || 0) - (a.likes_count || 0);
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const canModerate =
    userRole === 'owner' ||
    userRole === 'admin' ||
    userRole === 'moderator';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-100 p-1">
          <button
            onClick={() => setSortBy('recent')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              sortBy === 'recent'
                ? 'bg-teal-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <i className="ri-time-line mr-1"></i>Recent
          </button>
          <button
            onClick={() => setSortBy('popular')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              sortBy === 'popular'
                ? 'bg-teal-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <i className="ri-fire-line mr-1"></i>Popular
          </button>
        </div>
        <div className="flex items-center gap-2">
          {realtimePosts.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-500"></span>
              </span>
              Live
            </span>
          )}
          <span className="text-xs text-gray-400">
            {mergedPosts.length} moment
            {mergedPosts.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {!localIsMember ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-line text-2xl text-teal-500"></i>
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            Join to See Moments
          </h3>
          <p className="text-sm text-gray-500">
            Become a member to view and share moments with the group.
          </p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-quill-pen-line text-2xl text-gray-300"></i>
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            No moments yet
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Be the first to share something with the group!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post) => (
            <MomentCard
              key={post.id}
              post={post}
              members={members}
              currentUserId={currentUserId}
              canModerate={canModerate}
              onPinPost={onPinPost}
              onDeletePost={onDeletePost}
              isNew={newPostIds.has(post.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscussionFeed;
